import type { DialogRoot } from "@base-ui/react/dialog";
import { ModalLayout } from "@/components/modal-layout/modal-layout";
import { Dialog, DialogPopup } from "@/components/ui/dialog";
import type {
    ConfirmModalDescriptor,
    InfoModalDescriptor,
    SimpleModalDescriptor,
} from "@/services/simple-modal/simple-modal-types";
import { useStore } from "@/store/store";
import { ConfirmSimpleModal } from "./components/confirm-simple-modal";
import { InfoSimpleModal } from "./components/info-simple-modal";
import { rejectConfirm, resolveConfirm, resolveInfo } from "./resolver-registry";

const finishConfirm = (modal: ConfirmModalDescriptor, value: boolean): void => {
    resolveConfirm(modal.requestId, value);
    useStore.getState().closeSimpleModal();
};

const finishConfirmError = (modal: ConfirmModalDescriptor, reason: unknown): void => {
    rejectConfirm(modal.requestId, reason);
    useStore.getState().closeSimpleModal();
};

const finishInfo = (modal: InfoModalDescriptor): void => {
    resolveInfo(modal.requestId);
    useStore.getState().closeSimpleModal();
};

// Ensures that we resolve the modal state regardless of how the modal is closed
const handleOpenChange = (
    modal: SimpleModalDescriptor,
    nextOpen: boolean,
    details: DialogRoot.ChangeEventDetails,
): void => {
    if (nextOpen) {
        return;
    }
    if (!modal.dismissible) {
        details.cancel();
        return;
    }
    if (modal.type === "confirm") {
        // X / backdrop click does not run onDismiss.
        finishConfirm(modal, false);
        return;
    }
    finishInfo(modal);
};

export function SimpleModal() {
    const descriptor = useStore((s) => s.simpleModalDescriptor);
    const isOpen = useStore((s) => s.isSimpleModalOpen);

    return (
        <Dialog
            disablePointerDismissal={descriptor ? !descriptor.dismissible : true}
            onOpenChange={(open, eventDetails) => descriptor && handleOpenChange(descriptor, open, eventDetails)}
            onOpenChangeComplete={(open) => {
                // Guard against rapid open→close→open: a new modal may have opened before this
                // fires, so only clear content if the dialog is still genuinely closed.
                if (!open && !useStore.getState().isSimpleModalOpen) {
                    useStore.getState().clearSimpleModalDescriptor();
                }
            }}
            open={isOpen}
        >
            <DialogPopup showCloseButton={false} size={"md"}>
                {descriptor && (
                    <ModalLayout
                        key={descriptor.requestId}
                        title={descriptor.title ?? undefined}
                        onClickClose={
                            descriptor.dismissible
                                ? () =>
                                      handleOpenChange(descriptor, false, {
                                          cancel: () => {},
                                      } as DialogRoot.ChangeEventDetails)
                                : undefined
                        }
                    >
                        {descriptor.type === "confirm" ? (
                            <ConfirmSimpleModal
                                modal={descriptor}
                                onCancel={() => finishConfirm(descriptor, false)}
                                onConfirm={() => finishConfirm(descriptor, true)}
                                onError={(reason) => finishConfirmError(descriptor, reason)}
                            />
                        ) : (
                            <InfoSimpleModal modal={descriptor} onAcknowledge={() => finishInfo(descriptor)} />
                        )}
                    </ModalLayout>
                )}
            </DialogPopup>
        </Dialog>
    );
}
